import React from 'react'
import './daily-generation-report.scss'
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Autocomplete,
  ToggleButton,
  ToggleButtonGroup,
  TableSortLabel,
  TablePagination,
} from '@mui/material'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  FileUpload as FileUploadIcon,
  CalendarToday as CalendarTodayIcon,
} from '@mui/icons-material'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import DGRForm from './components/DGRForm/DGRForm'
import DGRDetail from './components/DGRDetail/DGRDetail'
import ImportDGRModal from './components/ImportDGRModal/ImportDGRModal'
import SolarPlantIcon from '../../assets/images/solar plant_3.svg'
import Snackbar from '@mui/material/Snackbar'
import Alert from '@mui/material/Alert'

const DGRDesktop = ({ logic }) => {
  const { state, setters, actions, permissions } = logic
  const { can } = permissions
  const [page, setPage] = React.useState(0)
  const [rowsPerPage, setRowsPerPage] = React.useState(10)

  const {
    sortedReports, loading, plantLoading, selectedReport, showForm, showDetail,
    reportToDelete, showDeleteModal, editMode, selectedPlant, plants,
    selectedDate, importModalOpen, importStatus, filterMode, dateSortDirection,
    startDate, endDate
  } = state

  const {
    handleCreateReport, handleEditReport, handleViewReport, handleDeleteReport,
    confirmDeleteReport, handleCancelDelete, handleFormSubmit, handleFormClose,
    handleDetailClose, handleOpenImportModal, handleCloseImportModal, handleImportSuccess,
    toggleDateSort
  } = actions

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    )
  }

  return (
    <div className="daily-generation-report">
      <div className="dgr-header">
        <Typography variant="h4" component="h1" className="page-title">
          Daily Generation Report (DGR)
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {can('daily_generation_report.create') && (
            <Button
              variant="contained"
              startIcon={<FileUploadIcon />}
              onClick={handleOpenImportModal}
              className="import-dgr-btn"
              sx={{
                backgroundColor: '#5968b7',
                '&:hover': {
                  backgroundColor: '#4a5a9e',
                },
              }}
            >
              Import
            </Button>
          )}
          {can('daily_generation_report.create') && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleCreateReport}
              className="create-dgr-btn"
            >
              New Entry
            </Button>
          )}
        </Box>
      </div>

      {/* Filters Section */}
      <Card className="filters-card" sx={{ mt: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <Autocomplete
                size="small"
                options={plants}
                getOptionLabel={(option) => option.plantName || ''}
                value={selectedPlant}
                onChange={(event, newValue) => {
                  setters.setSelectedPlant(newValue)
                }}
                loading={plantLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Plant Name"
                    placeholder="Select a plant..."
                    sx={{ minWidth: 300 }}
                  />
                )}
                sx={{ minWidth: 300 }}
              />
              {filterMode === 'date' ? (
                //Single date picker for Date mode
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Report Date"
                    format="DD-MM-YYYY"
                    value={selectedDate}
                    onChange={(date) => setters.setSelectedDate(date)}
                    slotProps={{
                      textField: { size: 'small', sx: { minWidth: 160 } },
                    }}
                  />
                </LocalizationProvider>
              ) : (
                //Date range pickers for Plant mode
                <>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="From Date"
                      format="DD-MM-YYYY"
                      value={startDate}
                      onChange={(date) => setters.setStartDate(date)}
                      maxDate={endDate}
                      slotProps={{
                        textField: { size: 'small', sx: { minWidth: 160 } },
                      }}
                    />
                  </LocalizationProvider>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="To Date"
                      format="DD-MM-YYYY"
                      value={endDate}
                      onChange={(date) => setters.setEndDate(date)}
                      minDate={startDate}
                      slotProps={{
                        textField: { size: 'small', sx: { minWidth: 160 } },
                      }}
                    />
                  </LocalizationProvider>
                </>
              )}
            </Box>
            <Box>
              <ToggleButtonGroup
                value={filterMode}
                exclusive
                onChange={(event, newMode) => {
                  if (newMode !== null) {
                    setters.setFilterMode(newMode)
                  }
                }}
                size="small"
                sx={{
                  '& .MuiToggleButton-root': {
                    color: '#5968b7',
                    borderColor: '#5968b7',
                    px: 1.5,
                    py: 0.5,
                    '& img': {
                      width: 18,
                      height: 18,
                      transition: 'filter 150ms ease',
                      display: 'inline-block',
                      verticalAlign: 'middle',
                    },
                    '&.Mui-selected': {
                      backgroundColor: '#5968b7',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: '#4a5a9e',
                      },
                      '& img': {
                        filter: 'brightness(0) invert(1)'
                      },
                    },
                    '&:hover': {
                      backgroundColor: 'rgba(89, 104, 183, 0.1)',
                    },
                  },
                }}
              >
                <ToggleButton value="date" aria-label="date mode">
                  <CalendarTodayIcon fontSize="small" />
                </ToggleButton>
                <ToggleButton value="plant" aria-label="plant mode">
                  <img
                    src={SolarPlantIcon}
                    alt="plant"
                    style={{ width: 18, height: 18 }}
                  />
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Reports Table */}
      <Card className="dgr-table-card" sx={{ mt: 2 }}>
        <CardContent>
          <Box sx={{ width: '100%', overflowX: 'auto' }}>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Sr. No.</TableCell>
                    <TableCell
                      sortDirection={dateSortDirection}
                      onClick={toggleDateSort}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                    >
                      <TableSortLabel
                        active
                        direction={dateSortDirection}
                      >
                        Date
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Plant Name</TableCell>
                    <TableCell>Plant Capacity (kWp)</TableCell>
                    <TableCell>Plant Location</TableCell>
                    <TableCell>GTI (W/m²)</TableCell>
                    <TableCell>Act Yield (kWh/kWp)</TableCell>
                    <TableCell>Act Generation (kWh)</TableCell>
                    <TableCell>PR (%)</TableCell>
                    <TableCell>Remarks</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedReports
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((report, index) => (
                      <TableRow key={report.id} hover>
                        <TableCell>{index + 1 + page * rowsPerPage}</TableCell>
                        <TableCell>
                          {dayjs(report.date || report.reportDate).isValid()
                            ? dayjs(report.date || report.reportDate).format('DD-MM-YYYY')
                            : 'N/A'}
                        </TableCell>
                        <TableCell>{report.plantName || 'N/A'}</TableCell>
                        <TableCell>{report.plantCapacity ?? 'N/A'}</TableCell>
                        <TableCell
                          sx={{
                            maxWidth: 300,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                          title={report.plantLocation || ''}
                        >
                          {report.plantLocation || 'N/A'}
                        </TableCell>
                        <TableCell>
                          {report.gti !== null && report.gti !== undefined && isFinite(Number(report.gti))
                            ? Number(report.gti).toFixed(2)
                            : 'N/A'}
                        </TableCell>
                        <TableCell>
                          {report.actYield !== null && report.actYield !== undefined && isFinite(Number(report.actYield))
                            ? Number(report.actYield).toFixed(2)
                            : 'N/A'}
                        </TableCell>
                        <TableCell>
                          {report.actGeneration !== null && report.actGeneration !== undefined && isFinite(Number(report.actGeneration))
                            ? Number(report.actGeneration).toFixed(2)
                            : 'N/A'}
                        </TableCell>
                        <TableCell>
                          {report.pr !== null && report.pr !== undefined && isFinite(Number(report.pr))
                            ? Number(report.pr).toFixed(2)
                            : 'N/A'}
                        </TableCell>
                        <TableCell>
                          {report.remarks ? report.remarks.substring(0, 30) + '...' : 'N/A'}
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => handleViewReport(report)}
                            title="View Details"
                          >
                            <ViewIcon />
                          </IconButton>
                          {can('daily_generation_report.update') && (
                            <IconButton
                              size="small"
                              onClick={() => handleEditReport(report)}
                              title="Edit Report"
                            >
                              <EditIcon />
                            </IconButton>
                          )}
                          {can('daily_generation_report.delete') && (
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteReport(report)}
                              title="Delete Report"
                            >
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  {sortedReports.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={11} align="center">
                        <Typography variant="body2" color="textSecondary">
                          No reports found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={sortedReports.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(event, newPage) => setPage(newPage)}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10))
                setPage(0)
              }}
              sx={{
                borderTop: '1px solid #e0e0e0',
                '& .MuiTablePagination-toolbar': {
                  px: { xs: 1, sm: 2 },
                  minHeight: '52px',
                },
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                  fontSize: '0.875rem',
                  color: 'text.secondary',
                  mb: 0,
                },
                '& .MuiTablePagination-select': {
                  fontSize: '0.875rem',
                },
              }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* DGR Form Dialog */}
      <Dialog
        open={showForm}
        onClose={(event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          handleFormClose()
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editMode ? 'Edit Daily Generation Report' : 'Create New Daily Generation Report'}
          <IconButton
            onClick={handleFormClose}
            style={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DGRForm
            report={selectedReport}
            onSubmit={handleFormSubmit}
            onCancel={handleFormClose}
          />
        </DialogContent>
      </Dialog>

      {/* DGR Detail Dialog */}
      <Dialog
        open={showDetail}
        onClose={(event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          handleDetailClose()
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Daily Generation Report Details
          <IconButton
            onClick={handleDetailClose}
            style={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedReport && (
            <DGRDetail
              report={selectedReport}
              onEdit={() => {
                setters.setShowDetail(false)
                handleEditReport(selectedReport)
              }}
              onClose={handleDetailClose}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete DGR Modal */}
      <Dialog
        open={showDeleteModal}
        onClose={(event, reason) => {
          if (reason === 'backdropClick') return;
          handleCancelDelete()
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Delete Daily Generation Report
          <IconButton
            onClick={handleCancelDelete}
            style={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography>
            You are about to delete the report for{' '}
            <strong>
              {reportToDelete?.plantName} on{' '}
              {dayjs(reportToDelete?.reportDate || reportToDelete?.date).format('DD-MM-YYYY')}
            </strong>
            . Do you want to proceed? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteReport}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import DGR Modal */}
      <ImportDGRModal
        open={importModalOpen}
        onClose={handleCloseImportModal}
        onImportSuccess={handleImportSuccess}
      />

      {/* Import Status Snackbar */}
      <Snackbar
        open={importStatus.open}
        autoHideDuration={10000}
        onClose={() => setters.setImportStatus({ ...importStatus, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setters.setImportStatus({ ...importStatus, open: false })}
          severity={importStatus.severity}
          sx={{ width: '100%' }}
          variant="filled"
        >
          <div>{importStatus.message}</div>
          {importStatus.errors && importStatus.errors.length > 0 && (
            <div style={{ marginTop: 8 }}>
              {importStatus.errors.slice(0, 5).map((e, i) => (
                <div key={i} style={{ fontSize: 12 }}>
                  {`Row ${e.row} (${e.plantName}, ${e.date}): ${e.reason}`}
                </div>
              ))}
              {importStatus.errors.length > 5 && (
                <div style={{ fontSize: 12, marginTop: 4 }}>
                  ...and {importStatus.errors.length - 5} more error(s)
                </div>
              )}
            </div>
          )}
        </Alert>
      </Snackbar>
    </div>
  )
}

export default DGRDesktop
